import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { useApi } from "@/lib/api";

interface Session {
  id: string;
  title: string | null;
  topic: string | null;
  status: "active" | "archived";
  last_message_at: number | null;
  message_count: number;
  created_at: number;
}

export function SessionSidebar(props: { currentId: string | undefined; onClose?: () => void }) {
  const api = useApi();
  const nav = useNavigate();
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    const r = await api<{ sessions: Session[] }>("/api/chat/sessions");
    setSessions(r.sessions);
  }

  useEffect(() => {
    refresh();
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  async function newSession() {
    setCreating(true);
    try {
      const r = await api<{ session: Session }>("/api/chat/sessions", {
        method: "POST",
        body: JSON.stringify({}),
      });
      await refresh();
      nav(`/coach/${r.session.id}`);
      props.onClose?.();
    } finally {
      setCreating(false);
    }
  }

  async function archive(id: string) {
    await api(`/api/chat/sessions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "archived" }),
    });
    await refresh();
  }

  return (
    <aside className="flex h-full w-72 flex-col border-r border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-200 px-3 py-3">
        <div className="text-sm font-semibold">Chats</div>
        <button
          onClick={newSession}
          disabled={creating}
          className="rounded-lg bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          + New chat
        </button>
      </div>
      <ul className="flex-1 overflow-y-auto px-2 py-2">
        {sessions?.filter((s) => s.status === "active").map((s) => (
          <li key={s.id} className="group">
            <Link
              to={`/coach/${s.id}`}
              onClick={props.onClose}
              className={`block rounded-lg px-3 py-2 text-sm ${
                s.id === props.currentId
                  ? "bg-neutral-900 text-white"
                  : "text-neutral-700 hover:bg-neutral-100"
              }`}
            >
              <div className="truncate font-medium">{s.title ?? "New chat"}</div>
              {s.topic && (
                <div className={`text-xs ${s.id === props.currentId ? "text-neutral-300" : "text-neutral-500"}`}>
                  {s.topic}
                </div>
              )}
              <div className={`mt-0.5 text-[10px] ${s.id === props.currentId ? "text-neutral-400" : "text-neutral-400"}`}>
                {s.message_count} msgs · {s.last_message_at ? relativeTime(s.last_message_at) : "not started"}
              </div>
            </Link>
            <button
              onClick={() => archive(s.id)}
              className="ml-3 mt-0.5 hidden text-[10px] text-neutral-400 hover:text-neutral-700 group-hover:inline"
            >
              archive
            </button>
          </li>
        ))}
        {sessions?.filter((s) => s.status === "archived").length ? (
          <li className="mt-3 px-3 text-[10px] uppercase tracking-wider text-neutral-400">Archived</li>
        ) : null}
        {sessions?.filter((s) => s.status === "archived").map((s) => (
          <li key={s.id}>
            <Link
              to={`/coach/${s.id}`}
              onClick={props.onClose}
              className="block truncate rounded-lg px-3 py-1.5 text-xs text-neutral-400 hover:bg-neutral-100"
            >
              {s.title ?? "New chat"}
            </Link>
          </li>
        ))}
        {sessions && sessions.length === 0 && (
          <li className="px-3 py-6 text-center text-xs text-neutral-400">No chats yet</li>
        )}
      </ul>
    </aside>
  );
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ts).toLocaleDateString("zh-CN");
}
